import {
    ArmoryLoadout,
    LoadoutStats,
    PowerDieColor,
    LoadoutStatsWithRequirements,
    Argonaut,
    KratosTokens,
    SimulationConfig,
} from '../types';
import { deepCopy } from './data-replication';
import { addKratosTokens } from './kratos-tokens';

export function convertToLoadoutStats(
    loadout: ArmoryLoadout,
    kratosPool?: KratosTokens,
    config?: SimulationConfig,
): LoadoutStats {
    let loadoutStats: LoadoutStats = {} as LoadoutStats;
    const loadoutCopy = deepCopy(loadout);

    const rageIncrement = (config?.autoIncrementRage ? 1 : 0) + (kratosPool?.rouse || 0);
    loadoutCopy.argonaut.rage = Math.min(loadoutCopy.argonaut.rage + rageIncrement, 9);

    const statsSources = [
        loadoutCopy.titan.stats,
        loadoutCopy.kratosTable.stats,
        loadoutCopy.weapon.stats,
        ...loadoutCopy.gear.map((g) => g.stats),
        ...loadoutCopy.mnemos.map((m) => m.stats),
    ];

    statsSources.forEach((statsArray) => {
        statsArray?.forEach((stats) => {
            if (doesArgonautMeetRequirements(loadoutCopy.argonaut, stats)) {
                loadoutStats = addLoadoutStats(loadoutStats, stats as Partial<LoadoutStats>);
            }
        });
    });
    loadoutStats = addLoadoutStats(loadoutStats, loadout.argonaut);

    const attackReRollsFromFate = 9 - loadoutCopy.argonaut.fate;
    loadoutStats.attackReRolls = Math.min(loadoutStats.attackReRolls + attackReRollsFromFate, loadoutStats.attackDice);

    // Despair penalty: 1 despair → -1 precision, 2+ despair → -3 precision
    const despair = loadoutCopy.argonaut.despair ?? 0;
    if (despair >= 2) {
        loadoutStats.precisionBonus = (loadoutStats.precisionBonus ?? 0) - 3;
    } else if (despair === 1) {
        loadoutStats.precisionBonus = (loadoutStats.precisionBonus ?? 0) - 1;
    }

    return {
        ...loadoutStats,
        name: loadoutCopy.name,
    };
}

function doesArgonautMeetRequirements(argonaut: Argonaut, stats: LoadoutStatsWithRequirements) {
    const minTri = stats.requirements.minTriskellion;
    const maxTri = stats.requirements.maxTriskellion;
    const minTokens = stats.requirements.minArgonautTokens;

    const meetsTriskellion =
        argonaut.rage >= (minTri.rage ?? 0) &&
        argonaut.fate >= (minTri.fate ?? 0) &&
        argonaut.danger >= (minTri.danger ?? 0) &&
        argonaut.rage <= (maxTri.rage ?? 9) &&
        argonaut.fate <= (maxTri.fate ?? 9) &&
        argonaut.danger <= (maxTri.danger ?? 9);

    if (!meetsTriskellion) return false;

    if (minTokens) {
        for (const [key, threshold] of Object.entries(minTokens)) {
            if ((argonaut[key as keyof typeof minTokens] ?? 0) < (threshold ?? 0)) {
                return false;
            }
        }
    }

    return true;
}

export function addLoadoutStats(a: Partial<LoadoutStats> = {}, b: Partial<LoadoutStats> = {}): LoadoutStats {
    return {
        name: '',
        attackDice: (a.attackDice ?? 0) + (b.attackDice ?? 0),
        attackReRolls: (a.attackReRolls ?? 0) + (b.attackReRolls ?? 0),
        autoHits: (a.autoHits ?? 0) + (b.autoHits ?? 0),
        autoTokens: addKratosTokens(a.autoTokens, b.autoTokens),
        maxHits: (a.maxHits ?? 0) + (b.maxHits ?? 0),
        // TODO - if gear adds power dice per hit (e.g. +1 red power die per hit) then need to update this function
        overridePowerDice: (a.overridePowerDice || b.overridePowerDice) ?? false,
        perHitPowerDiceOverride: addArrayOfArrays(a.perHitPowerDiceOverride ?? [], b.perHitPowerDiceOverride ?? []),
        perHitPowerDice: (a.perHitPowerDice ?? []).concat(b.perHitPowerDice ?? []),
        powerBonus: (a.powerBonus ?? 0) + (b.powerBonus ?? 0),
        powerDice: (a.powerDice ?? []).concat(b.powerDice ?? []),
        powerReRolls: (a.powerReRolls ?? 0) + (b.powerReRolls ?? 0),
        precisionBonus: (a.precisionBonus ?? 0) + (b.precisionBonus ?? 0),
    };
}

function addArrayOfArrays(arr1: PowerDieColor[][], arr2: PowerDieColor[][]): PowerDieColor[][] {
    const maxLength = Math.max(arr1.length, arr2.length);
    const result: PowerDieColor[][] = [];

    for (let i = 0; i < maxLength; i++) {
        const innerArr1 = arr1[i] || [];
        const innerArr2 = arr2[i] || [];
        result.push([...innerArr1, ...innerArr2]);
    }

    return result;
}
